/**
 * Every explanation the Add Stories dialog shows, in one place (PLAT-11940
 * DE4). Labels are Title Case, explanations sentence case — one sentence per
 * option, saying what the choice does rather than restating it.
 *
 * The audio explanations follow what the backend actually receives
 * (`StoryCreationService.startProcessingUploadedVideo`): "process" sends
 * `REMOVE_MUSIC` for produced content and `CORRECT_AUDIO` for raw content;
 * "keep" sends no preset.
 */
import {
  ONE_VIDEO_ONLY_MESSAGE as ONE_FILE_ONLY,
  UNSUPPORTED_VIDEO_MESSAGE as UNSUPPORTED_FILE,
} from '@src/components/common/VideoDropBox';

import type { StoryVideoMusicStrategy } from './useStoryCreator';

export type StoryType = 'raw' | 'produced';

export type ChoiceCopy<T extends string> = {
  value: T;
  label: string;
  description: string;
};

export const UPLOAD_STEP_TITLE = 'Upload a Video';
export const ZOOM_STEP_TITLE = 'Import from Zoom';
export const DETAILS_STEP_TITLE = 'Story Details';
export const stepSubtitle = (step: 1 | 2) => `Step ${step} of 2`;

export const MULTI_SUBTITLE = 'Combine several videos into one story';
export const URL_PLACEHOLDER =
  'Paste a link from Zoom, Google Drive or Dropbox';
export const URL_INVALID = 'Enter a full link, starting with https://';
export {
  ONE_VIDEO_ONLY_MESSAGE as ONE_FILE_ONLY,
  UNSUPPORTED_VIDEO_MESSAGE as UNSUPPORTED_FILE,
} from '@src/components/common/VideoDropBox';

export const acceptedFilesLine = (maxUploadSizeGB: number) =>
  `MP4, MOV or WebM · up to ${maxUploadSizeGB} GB`;

export const UPLOAD_STARTS_NOTE =
  'Your upload starts as soon as you pick a file. You can close this window and it keeps going.';

export const STORY_TYPE_LABEL = 'Story Type';
// Raw first: it is the default, and most uploads are recordings.
export const STORY_TYPE_OPTIONS: ChoiceCopy<StoryType>[] = [
  {
    value: 'raw',
    label: 'Raw Content',
    description: 'Unedited footage, like an interview or a recording.',
  },
  {
    value: 'produced',
    label: 'Produced Content',
    description: 'A finished, edited video.',
  },
];

/** Pre-selected so the common case is one click (Save). */
export const DEFAULT_STORY_TYPE: StoryType = 'raw';

export const AUDIO_LABEL: Record<StoryType, string> = {
  produced: 'Music',
  raw: 'Audio',
};

export const AUDIO_OPTIONS: Record<
  StoryType,
  ChoiceCopy<StoryVideoMusicStrategy>[]
> = {
  produced: [
    {
      value: 'process',
      label: 'Remove Music',
      description: 'Takes out background music so voices are clear.',
    },
    {
      value: 'keep',
      label: 'Keep Music',
      description: 'Keeps the soundtrack as it is.',
    },
  ],
  raw: [
    {
      value: 'process',
      label: 'Correct Audio',
      description: 'Cleans up the sound so voices are clear.',
    },
    {
      value: 'keep',
      label: 'Leave Audio',
      description: 'Keeps the sound exactly as recorded.',
    },
  ],
};

/**
 * Shown instead of the audio choice when the file is too large to process
 * (PLAT-11103): the only answer left is "keep", which the hook has already
 * picked, so a choice with one live option would be a question with no
 * answer to give.
 */
export const AUDIO_TOO_LARGE: Record<StoryType, string> = {
  produced: 'Files over 8 GB keep their music as it is.',
  raw: 'Files over 8 GB keep their sound exactly as recorded.',
};

export const STORY_NAME_LABEL = 'Story Name';
export const STORY_NAME_PLACEHOLDER = 'Name your story';
export const STORY_NAME_HELPER = 'You can rename it later.';
export const STORY_NAME_CLEARED_HELPER =
  'The file name looked like an id, so we left this blank.';
export const LANGUAGE_LABEL = 'Transcript Language';
export const FOLDER_LABEL = 'Add to Folder';

export const AFTER_SAVE_NOTE =
  'When you save, Arbor optimizes and transcribes your video. Follow its progress in the Story Manager.';

/**
 * A file name that carries no meaning to a person — a UUID, a camera counter
 * ("IMG_4521"), a recorder's timestamp id — is a worse default than an empty
 * field: people keep it rather than rename. Measured on the name the
 * dialog would pre-fill (`getStoryNameFromFileName`), with spaces removed.
 */
export function isIdLikeName(name: string): boolean {
  const compact = name.replace(/\s+/g, '');
  if (compact.length < 6) return false;
  const digits = compact.replace(/\D/g, '').length;
  if (digits / compact.length >= 0.5) return true;
  // UUID / hash fragments: only hex characters, with at least a few digits.
  return /^[0-9a-f]+$/i.test(compact) && digits >= 4;
}

const STORY_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const STORY_VIDEO_EXTENSIONS = /\.(mp4|mov|qt|webm)$/i;

/** A video the pipeline takes, by MIME type or — when the OS gives none — extension. */
export function isStoryVideoFile(file: File): boolean {
  return (
    STORY_VIDEO_TYPES.includes(file.type) ||
    (!file.type && STORY_VIDEO_EXTENSIONS.test(file.name))
  );
}

/**
 * What a page drop may hand the Add Stories dialog: exactly one video. The
 * dialog then runs the full pre-flight (size, container, audio) as it would
 * for its own drop box. Several files are refused rather than silently cut to
 * the first — combining videos is the dialog's Multiple Videos mode.
 */
export function checkDroppedStoryFiles(
  files: File[],
): { ok: true; file: File } | { ok: false; message: string } {
  if (files.length !== 1) return { ok: false, message: ONE_FILE_ONLY };
  if (!isStoryVideoFile(files[0])) {
    return { ok: false, message: UNSUPPORTED_FILE };
  }
  return { ok: true, file: files[0] };
}

export const dropZoneTitle = (folderName?: string) =>
  folderName ? `Drop to add to “${folderName}”` : 'Drop files here to upload';

export const dropZoneHint = (maxUploadSizeGB: number) =>
  `${acceptedFilesLine(maxUploadSizeGB)} · one video per story`;
